import { BoardID } from 'BoardID';
import sampleSize from 'lodash.samplesize';
import {
  createRandomMaterialPatternString,
  parsePatternString,
  scalePatternString,
  sumPattern,
  toPatternString,
} from 'Pattern';
import {
  BoardDetails,
  BoardType,
  Dimensions2D,
  Material,
  ParamsPanelSelection,
  PlaidParams,
  Renderable,
  RenderableMetadata,
  Settings,
  TwoArray,
  Units,
} from 'Types';

import BoardBase from 'boardClasses/BoardBase';
import { randomBool, sample, scaleNumber } from 'utils/utils';

export default class Plaid extends BoardBase<BoardType.Plaid> {
  public static isDevOnly = true;

  constructor(id: BoardID, name: string, params: PlaidParams, units?: Units) {
    super(BoardType.Plaid, id, name, params, units);
  }

  static createRandom(
    materials: TwoArray<Material>,
    _size: Dimensions2D
  ): PlaidParams {
    const [background, wood1, wood2] = sampleSize(materials, 3);
    const [colWidth, numCols] = sample([
      [1, 11],
      [1.25, 9],
      [1.625, 7],
      [2.25, 5],
    ]);
    const [rowHeight, numRows] = sample([
      [1, 17],
      [1.125, 15],
      [1.25, 13],
      [1.5, 11],
    ]);
    const hOn1 = randomBool();
    const vOn1 = randomBool();

    return {
      background,
      wood1,
      wood2,
      startOn: sample([
        'background',
        'wood1/background',
        'wood1/wood2',
        'wood2',
      ]),
      colWidth,
      rowHeight,
      numCols,
      numRows,
      hStripe: createRandomMaterialPatternString(
        1 / 8,
        3 / 8,
        rowHeight / 3,
        materials
      ),
      vStripe: createRandomMaterialPatternString(
        1 / 8,
        3 / 8,
        colWidth / 3,
        materials
      ),
      isHStripeOn1: hOn1,
      isHStripeOn2: hOn1 ? randomBool() : true,
      isVStripeOn1: vOn1,
      isVStripeOn2: vOn1 ? randomBool() : true,
    };
  }

  private get hStripePattern() {
    return parsePatternString(this.params.hStripe);
  }

  private get vStripePattern() {
    return parsePatternString(this.params.vStripe);
  }

  private get hStripeSize() {
    return sumPattern(this.hStripePattern);
  }

  private get vStripeSize() {
    return sumPattern(this.vStripePattern);
  }

  // private get baseBoard() {
  //   const backgroundWood1Pattern: MaterialPattern = [];
  //   const wood1Wood2Pattern: MaterialPattern = [];

  //   for (let col = 0; col < this.params.numCols; ++col) {
  //     backgroundWood1Pattern.push({
  //       size: this.params.colWidth,
  //       key: this.getMaterialFromRowCol(0, col),
  //       flip: false,
  //     });
  //     wood1Wood2Pattern.push({
  //       size: this.params.colWidth,
  //       key: this.getMaterialFromRowCol(0, col),
  //       flip: false,
  //     });
  //   }

  //   return createAnonymousBoard(
  //     StripsAdvanced,
  //     {
  //       pattern: '' as SubBoardPatternString,
  //       subpatterns: [
  //         toPatternString(backgroundWood1Pattern),
  //         toPatternString(wood1Wood2Pattern),
  //       ],
  //       thickness: 0,
  //     },
  //     Units.IN
  //   );
  // }

  private getMaterialFromRowCol(row: number, col: number) {
    const isEvenRow = row % 2 === 0;
    const isEvenCol = col % 2 === 0;

    const { startOn, wood1, wood2, background } = this.params;

    let material = background;

    if (startOn === 'background') {
      if (isEvenRow && !isEvenCol) material = wood1;
      if (!isEvenRow && isEvenCol) material = wood1;
      if (!isEvenRow && !isEvenCol) material = wood2;
    } else if (startOn === 'wood1/background') {
      if (isEvenRow && isEvenCol) material = wood1;
      if (isEvenRow && !isEvenCol) material = wood2;
      if (!isEvenRow && !isEvenCol) material = wood1;
    } else if (startOn === 'wood1/wood2') {
      if (isEvenRow && isEvenCol) material = wood1;
      if (!isEvenRow && isEvenCol) material = wood2;
      if (!isEvenRow && !isEvenCol) material = wood1;
    } else if (startOn === 'wood2') {
      if (isEvenRow && isEvenCol) material = wood2;
      if (isEvenRow && !isEvenCol) material = wood1;
      if (!isEvenRow && isEvenCol) material = wood1;
    }

    return material;
  }

  getRenderables(): Array<Renderable<BoardType.Plaid>> {
    const {
      wood1,
      wood2,
      startOn,
      numRows,
      numCols,
      colWidth,
      rowHeight,
      isHStripeOn1,
      isHStripeOn2,
      isVStripeOn1,
      isVStripeOn2,
    } = this.params;

    let renderables: Array<Renderable<BoardType.Plaid>> = [];
    let rowTopOffset = 0;
    let renderableIndex = 0;

    for (let row = 0; row < numRows; ++row) {
      const isEvenRow = row % 2 === 0;
      const isWood1Row =
        startOn === 'background' || startOn === 'wood1/wood2'
          ? isEvenRow
          : !isEvenRow;
      const isWood2Row = !isWood1Row;
      const hasHStripe =
        this.hStripeSize > 0 &&
        ((isWood1Row && isHStripeOn1) || (isWood2Row && isHStripeOn2));

      let colLeftOffset = 0;

      for (let col = 0; col < numCols; ++col) {
        const isEvenCol = col % 2 === 0;
        const isWood1Col =
          startOn === 'background' || startOn === 'wood1/background'
            ? isEvenCol
            : !isEvenCol;
        const isWood2Col = !isWood1Col;
        const hasVStripe =
          this.vStripeSize > 0 &&
          ((isWood1Col && isVStripeOn1) || (isWood2Col && isVStripeOn2));

        const halfRowHeight = rowHeight / 2;
        const halfColWidth = colWidth / 2;
        const halfRowHeightLess = halfRowHeight - this.hStripeSize / 2;
        const halfColWidthLess = halfColWidth - this.vStripeSize / 2;

        let material = this.getMaterialFromRowCol(row, col);

        const type =
          material === wood2
            ? 'wood2'
            : material === wood1
            ? 'wood1'
            : 'background';

        // main renderable per square
        renderables.push({
          rect: {
            height: rowHeight,
            width: colWidth,
            left: colLeftOffset,
            top: rowTopOffset,
          },
          material,
          selectionSizeProp: 'both',
          metadata: {
            renderableIndex: renderableIndex++,
            rowIndex: row,
            colIndex: col,
            type,
          },
        });

        if (hasVStripe) {
          let offset = 0;
          // vStripe per square
          for (const { key, size } of this.vStripePattern) {
            renderables.push({
              rect: {
                height: rowHeight,
                width: size,
                top: rowTopOffset,
                left: colLeftOffset + halfColWidthLess + offset,
              },
              material: key,
              selectionSizeProp: 'width',
              metadata: {
                renderableIndex: renderableIndex++,
                rowIndex: row,
                colIndex: col,
                type: 'vStripe',
              },
            });

            offset += size;
          }
        }

        if (hasHStripe) {
          let offset = 0;
          // hStripe per square
          for (const { key, size } of this.hStripePattern) {
            renderables.push({
              rect: {
                height: size,
                width: colWidth,
                top: rowTopOffset + halfRowHeightLess + offset,
                left: colLeftOffset,
              },
              material: key,
              selectionSizeProp: 'height',
              metadata: {
                renderableIndex: renderableIndex++,
                rowIndex: row,
                colIndex: col,
                type: 'hStripe',
              },
            });

            offset += size;
          }
        }

        colLeftOffset += colWidth;
      }

      rowTopOffset += rowHeight;
    }

    return renderables;
  }

  getIsRenderableHighlighted(
    renderable: Renderable<BoardType.Plaid>,
    selection: Renderable<BoardType.Plaid>
  ): boolean {
    return renderable.metadata.type === selection.metadata.type;
  }

  getBoardDetails(settings: Settings): BoardDetails {
    // TODO
    return { idealStartingStock: [] };
  }

  handleSelectedMaterialChange(
    { metadata }: Renderable<BoardType.Plaid>,
    material: Material
  ) {
    switch (metadata.type) {
      case 'background': {
        return this.updateParams({ background: material });
      }
      case 'wood1': {
        return this.updateParams({ wood1: material });
      }
      case 'wood2': {
        return this.updateParams({ wood2: material });
      }
      case 'hStripe': {
        const pattern = parsePatternString(this.params.hStripe);
        pattern[0].key = material;
        return this.updateParams({ hStripe: toPatternString(pattern) });
      }
      case 'vStripe': {
        const pattern = parsePatternString(this.params.vStripe);
        pattern[0].key = material;
        return this.updateParams({ vStripe: toPatternString(pattern) });
      }
    }
  }

  handleSelectedSizeChange(
    { metadata }: Renderable<BoardType.Plaid>,
    size: number,
    dimension: 'height' | 'width'
  ) {
    if (metadata.type === 'hStripe') {
      const pattern = parsePatternString(this.params.hStripe);
      pattern[0].size = size;
      return this.updateParams({ hStripe: toPatternString(pattern) });
    }
    if (metadata.type === 'vStripe') {
      const pattern = parsePatternString(this.params.vStripe);
      pattern[0].size = size;
      return this.updateParams({ vStripe: toPatternString(pattern) });
    }

    return this.updateParams(
      dimension === 'height' ? { rowHeight: size } : { colWidth: size }
    );
  }

  // No preview params panel selection
  previewSelectionToParamsPanelSelection(): null | ParamsPanelSelection<BoardType.Plaid> {
    return null;
  }

  // No preview params panel selection
  paramsPanelSelectionToPreviewSelection(): null | RenderableMetadata<BoardType.Plaid> {
    return null;
  }

  scaleParams(params: PlaidParams, scaleFactor: number): PlaidParams {
    return {
      ...params,
      rowHeight: scaleNumber(params.rowHeight, scaleFactor),
      colWidth: scaleNumber(params.colWidth, scaleFactor),
      hStripe: scalePatternString(params.hStripe, scaleFactor),
      vStripe: scalePatternString(params.vStripe, scaleFactor),
    };
  }
}
